import {
  ButtonBase,
  Grid,
  Hidden,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import {
  colors,
  DefaultCard,
  DisplayItem,
  FormHookResult,
  FormValueTypes,
  InstructionsLayout,
  SubTitle2Text,
} from 'design-system';
import { HelpCircle } from 'react-feather';
import { cities } from 'src/data/citiesCr.json';
import { states } from 'src/data/statesCr.json';
import { townships } from 'src/data/townshipsCr.json';
import {
  isAlphaNumericForAddress,
  isAlphaNumericInSpanish,
} from 'src/functions/isAlphaNumeric';
// subcomponents
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { AnyAction } from 'redux';
import { COSTA_RICA } from 'src/constants/countryCode';
import {
  addLegalPersonEquifax,
  getCatalogToOptions,
} from 'src/functions/equifax';
import { NaturalPersonEquifaxResponse } from 'src/models/responseTypes/NaturalPersonEquifaxResponse';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';
import processRequest from 'src/services/processRequest';
import { REQUEST_SEARCH_VALIDATE_PERSON } from 'src/store/person/requestIdentifiers';
import {
  makeDateField,
  makeDropDown,
  makeSearchableDropDown,
  makeTextField,
  makeTextIdField,
} from '../../../MakeFormFields';
import PersonValidationAlert from '../../EmissionPolicy/components/PersonValidationAlert';
import PersonValidationModal from '../../EmissionPolicy/components/PersonValidationModal';
import { LoadingEquifaxContent } from '../../EmissionPolicy/NaturalPersonData/components/LoadingEquifax';

const useStyles = makeStyles((theme: Theme) => ({
  instructionsWrapper: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(-1),
    textAlign: 'center',
  },
  customButtonText: {
    color: colors.bright_turquoise,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '19px',
  },
  customAlert: {
    marginBottom: '12px',
  },
}));

const itemList: {
  listInputId: string;
  listInputName: string;
  listLabel: string;
  helperText: string;
}[] = [
  {
    listInputId: 'sales-web-inspection-label-finalbeneficiaryphone1',
    listInputName: 'Phone1',
    listLabel: 'Celular',
    helperText: '',
  },
  {
    listInputId: 'sales-web-inspection-label-finalbeneficiaryphone2',
    listInputName: 'Phone2',
    listLabel: 'Teléfono fijo (opcional)',
    helperText: '',
  },
];

type RenderIdHelpProps = {
  onClick: (val: boolean) => void;
  showHelp: boolean;
  classes: ReturnType<typeof useStyles>;
};
function renderIdHelpSection({
  onClick,
  showHelp,
  classes,
}: RenderIdHelpProps): JSX.Element {
  return (
    <div className={classes.instructionsWrapper}>
      <Grid item xs={12}>
        <Typography
          id="sales-web-inspection-label-finalbeneficiaryidinstruction"
          variant="caption"
          style={{ color: colors.abbey }}
        >
          Sin espacios ni guiones (ej. 888888888), tal como aparece en tu
          identificación.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {showHelp && (
          <ButtonBase
            id="sales-web-inspection-button-finalbeneficiaryidinstruction"
            data-testid="instructions-button"
            disableRipple
            onClick={(): void => onClick(true)}
          >
            <Grid container alignItems="center" justifyContent="center">
              <Typography className={classes.customButtonText}>
                ¿Cómo debes ingresar la cédula?
              </Typography>
              &nbsp;
              <HelpCircle color={colors.bright_turquoise} />
            </Grid>
          </ButtonBase>
        )}
      </Grid>
    </div>
  );
}

type UpdateMultipleInputType = (newInputdata: {
  [key: string]: FormValueTypes;
}) => void;

type Props = {
  prefixKey?: string;
  getInputProps: FormHookResult;
  updateMultipleInputs: UpdateMultipleInputType;
  idTypes: DisplayItem[];
  entityIsInvalid?: boolean;
  requestingBeneficiaryBirthPlace: boolean;
  requestingBeneficiaryNationality: boolean;
  requestingBeneficiaryOccupations: boolean;
  requestingBeneficiaryCountries: boolean;
  requestingBeneficiaryStates: boolean;
  beneficiaryBirthPlaceOptions: DisplayItem[];
  beneficiaryNationalityOptions: DisplayItem[];
  beneficiaryOccupationOptions: DisplayItem[];
  beneficiaryCountriesOptions: DisplayItem[];
  beneficiaryStatesOptions: DisplayItem[];
  beneficiaryCityOptions: DisplayItem[];
  beneficiaryTownshipOptions: DisplayItem[];
  isIndividualQuotation?: boolean;
  personValidateData: PersonDataResponse;
  countryStates: GeneralCatalogItem[];
  validatePersonAction: (
    idNumber: string,
    idType: string,
  ) => Promise<AnyAction>;
  setEntityIsInvalid: Dispatch<SetStateAction<boolean>>;
  setBeneficiaryCityOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  setBeneficiaryTownshipOptions: Dispatch<SetStateAction<DisplayItem[]>>;
};

const FinalBeneficiaryCard = ({
  // used in cases where the property names overlap with other property names in the same context
  prefixKey = '',
  getInputProps,
  updateMultipleInputs,
  idTypes,
  entityIsInvalid,
  requestingBeneficiaryBirthPlace,
  requestingBeneficiaryOccupations,
  requestingBeneficiaryCountries,
  requestingBeneficiaryStates,
  beneficiaryBirthPlaceOptions,
  beneficiaryOccupationOptions,
  beneficiaryCountriesOptions,
  beneficiaryStatesOptions,
  beneficiaryCityOptions,
  beneficiaryTownshipOptions,
  isIndividualQuotation,
  personValidateData,
  validatePersonAction,
  setEntityIsInvalid,
  setBeneficiaryCityOptions,
  setBeneficiaryTownshipOptions,
}: Props): JSX.Element => {
  const addressCountry = getInputProps(`${prefixKey}CountryId`).value as string;
  const isNotFromCostaRica = addressCountry !== COSTA_RICA;
  const classes = useStyles();
  const [isInstructionsVisible, setIsInstructionsVisible] = useState(false);
  const requestingPerson = useNetworkObserver(REQUEST_SEARCH_VALIDATE_PERSON);
  const [refetch, setRefetch] = useState(false);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [errors, setErrors] = useState<{ error: boolean; errorText: string }>({
    error: false,
    errorText: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const showHelp =
    (getInputProps(`${prefixKey}IdType`).value as string) === 'N';

  const onChangeBeneficiaryCountry = (): void => {
    updateMultipleInputs({
      beneficiaryState: '',
      beneficiaryStateId: '',
      beneficiaryCity: '',
      beneficiaryCityId: '',
      beneficiaryTownship: '',
      beneficiaryTownshipId: '',
    });
  };
  const onChangeBeneficiaryState = (newVal: string): void => {
    updateMultipleInputs({
      beneficiaryCity: '',
      beneficiaryCityId: '',
      beneficiaryTownship: '',
      beneficiaryTownshipId: '',
    });
    const state = states.find((item) => item.code === newVal);
    setBeneficiaryCityOptions(getCatalogToOptions(cities, state?.id));
  };
  const onChangeBeneficiaryCity = (newVal: string): void => {
    updateMultipleInputs({
      beneficiaryTownship: '',
      beneficiaryTownshipId: '',
    });
    const stateInput = getInputProps(`${prefixKey}StateId`);
    const stateValue = stateInput.value as string;
    const state = states.find((item) => item.code === stateValue);
    const citiesFilter = cities.filter((item) => item.parentId === state?.id);
    const city = citiesFilter.find((item) => item.code === newVal);
    setBeneficiaryTownshipOptions(getCatalogToOptions(townships, city?.id));
  };

  useEffect(() => {
    if (requestingPerson === 'success' && refetch) {
      setRefetch(false);
    }
  }, [requestingPerson]);

  useEffect(() => {
    if (requestingPerson === 'success') {
      setShowValidationAlert(!personValidateData.isValidPerson);
      setOpenValidationModal(!personValidateData.isValidPerson);
      if (
        !personValidateData.isValidPerson ||
        (personValidateData.personExist && !personValidateData.personInnova)
      ) {
        setErrors({
          error: true,
          errorText: 'Ingrese una identificación válida',
        });
        setEntityIsInvalid(true);
      }
    }
  }, [requestingPerson]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        getInputProps(`${prefixKey}IdNumber`).value &&
        !getInputProps(`${prefixKey}IdNumber`).error
      ) {
        setIsLoading(true);
        const idNumber = getInputProps(`${prefixKey}IdNumber`).value as string;
        const idType = getInputProps(`${prefixKey}IdType`).value as string;
        validatePersonAction(idNumber, idType);
        setEntityIsInvalid(false);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [getInputProps(`${prefixKey}IdNumber`).value]);

  const legalPersonData = useCallback(async () => {
    const person = await processRequest<
      NetworkResponse<NaturalPersonEquifaxResponse>
    >({
      method: 'GET',
      url: `/Person/personinformation?ruc=${
        getInputProps(`${prefixKey}IdNumber`).value
      }`,
    });

    await addLegalPersonEquifax({
      person,
      getInputProps,
      prefixKey,
      setBeneficiaryCityOptions,
      setBeneficiaryTownshipOptions,
    });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (
      requestingPerson === 'success' &&
      personValidateData.isValidPerson &&
      getInputProps(`${prefixKey}IdType`).value === 'N'
    ) {
      legalPersonData();
    }
  }, [requestingPerson]);

  useEffect(() => {
    if (
      !personValidateData.isValidPerson ||
      getInputProps(`${prefixKey}IdType`).value !== 'N'
    ) {
      setIsLoading(false);
    }
  }, [personValidateData]);

  return (
    <>
      {/* Instructions modal */}
      {!isIndividualQuotation && (
        <InstructionsLayout
          id="finalbeneficiary"
          data-testid="instruction-modal"
          open={isInstructionsVisible}
          headerTitle="¿Cómo ingresar tu cédula?"
          title="Número de cédula"
          content={
            <img
              id="sales-web-inspection-image-finalbeneficiaryinstructioncontent"
              alt="cedula"
              src="/images/id_format_guide.svg"
            />
          }
          instructions={
            <Typography
              id="sales-web-inspection-label-finalbeneficiaryinstructiontext"
              variant="body2"
              align="center"
              style={{ color: colors.abbey }}
            >
              Escribe tu número de cédula, como aparece en ella, sin espacios ni
              guiones.
            </Typography>
          }
          onCloseRequested={(): void => setIsInstructionsVisible(false)}
        />
      )}

      <PersonValidationAlert
        data-testid="person-validation-alert"
        onCloseAlert={() => setShowValidationAlert(false)}
        showAlert={showValidationAlert}
        className={classes.customAlert}
      />

      <PersonValidationModal
        data-testid="person-validation-modal"
        onClose={() => setOpenValidationModal(false)}
        open={openValidationModal}
      />

      {/* Form */}
      <DefaultCard title="Datos del representante legal">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeDropDown({
              inputId: 'sales-web-inspection-dropdown-finalbeneficiaryidtype',
              inputName: `${prefixKey}IdType`,
              label: 'Tipo de identificación',
              dropdownList: idTypes,
              getInputProps,
              isDisabled: entityIsInvalid,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeTextIdField({
              inputId: 'sales-web-inspection-input-finalbeneficiaryid',
              inputName: `${prefixKey}IdNumber`,
              label: 'Número de identificación',
              helperText:
                (getInputProps(`${prefixKey}IdType`).value as string) === 'U'
                  ? 'Debe iniciar con 1 y contener 12 caracteres'
                  : 'Sin espacios ni guiones (ej. 888888888)',
              isDisabled: entityIsInvalid,
              getInputProps: () => ({
                ...getInputProps(`${prefixKey}IdNumber`),
                error:
                  errors.error || getInputProps(`${prefixKey}IdNumber`).error,
                errorText:
                  errors.errorText ||
                  getInputProps(`${prefixKey}IdNumber`).errorText,
                onChange(newValue) {
                  getInputProps(`${prefixKey}IdNumber`).onChange(newValue);
                  setRefetch(true);
                  setErrors({
                    error: getInputProps(`${prefixKey}IdNumber`).error,
                    errorText: getInputProps(`${prefixKey}IdNumber`).errorText,
                  });
                },
              }),
            })}
          </Grid>
          {isLoading ? (
            <Grid item xs={12}>
              <LoadingEquifaxContent />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                {makeTextField({
                  inputId:
                    'sales-web-inspection-input-finalbeneficiaryfirstname',
                  inputName: `${prefixKey}FirstName`,
                  label: 'Nombres',
                  getInputProps,
                  isDisabled: entityIsInvalid,
                  shouldUpdate: isAlphaNumericInSpanish,
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {makeTextField({
                  inputId:
                    'sales-web-inspection-input-finalbeneficiarylastname',
                  inputName: `${prefixKey}LastName`,
                  label: 'Apellidos',
                  getInputProps,
                  isDisabled: entityIsInvalid,
                  shouldUpdate: isAlphaNumericInSpanish,
                })}
              </Grid>
              {isIndividualQuotation ? (
                <Grid item xs={12} md={6}>
                  {makeDateField({
                    inputId:
                      'sales-web-inspection-input-finalbeneficiarybirthdate',
                    label: 'Fecha de nacimiento',
                    inputName: `${prefixKey}Birthdate`,
                    getInputProps,
                    isDisabled: entityIsInvalid,
                  })}
                </Grid>
              ) : null}
              {isIndividualQuotation ? (
                <Grid item xs={12} md={6}>
                  {makeSearchableDropDown({
                    inputId:
                      'sales-web-inspection-input-finalbeneficiarybirthPlace',
                    label: 'País de nacimiento',
                    getInputProps,
                    isDisabled: entityIsInvalid,
                    inputName: `${prefixKey}BirthPlaceId`,
                    dropdownList: beneficiaryBirthPlaceOptions,
                    requesting: requestingBeneficiaryBirthPlace,
                  })}
                </Grid>
              ) : null}
              {!isIndividualQuotation && (
                <>
                  <Hidden mdUp>
                    <Grid item xs={12} container spacing={1}>
                      {renderIdHelpSection({
                        onClick: setIsInstructionsVisible,
                        showHelp,
                        classes,
                      })}
                    </Grid>
                  </Hidden>
                  <Hidden smDown>
                    <Grid
                      item
                      md={12}
                      container
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      {renderIdHelpSection({
                        onClick: setIsInstructionsVisible,
                        showHelp,
                        classes,
                      })}
                    </Grid>
                  </Hidden>
                </>
              )}

              {isIndividualQuotation ? (
                <Grid item xs={12} md={6}>
                  {makeSearchableDropDown({
                    inputId:
                      'sales-web-inspection-input-finalbeneficiaryoccupation',
                    label: 'Profesión',
                    getInputProps,
                    isDisabled: entityIsInvalid,
                    inputName: `${prefixKey}Occupation`,
                    dropdownList: beneficiaryOccupationOptions,
                    requesting: requestingBeneficiaryOccupations,
                  })}
                </Grid>
              ) : null}
              {isIndividualQuotation ? (
                <>
                  <Grid item xs={12} md={6}>
                    {makeTextField({
                      inputId:
                        'sales-web-inspection-input-finalbeneficiarycompanyrole',
                      label: 'Cargo dentro de la empresa',
                      inputName: `${prefixKey}CompanyRole`,
                      getInputProps,
                      isDisabled: entityIsInvalid,
                      shouldUpdate: isAlphaNumericInSpanish,
                    })}
                  </Grid>
                  {itemList.map(
                    ({ listInputId, listInputName, listLabel, helperText }) => (
                      <Grid key={listInputId} item xs={12} md={6}>
                        {makeTextField({
                          inputId: listInputId,
                          inputName: `${prefixKey}${listInputName}`,
                          label: listLabel,
                          getInputProps,
                          isDisabled: entityIsInvalid,
                          helperText,
                        })}
                      </Grid>
                    ),
                  )}
                </>
              ) : null}

              {isIndividualQuotation ? (
                <Grid item xs={12}>
                  <SubTitle2Text>
                    Domicilio del Representante Legal
                  </SubTitle2Text>
                  <br />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      {makeSearchableDropDown({
                        inputId:
                          'sales-web-inspection-input-finalbeneficiarycountry',
                        label: 'País',
                        getInputProps,
                        inputName: `${prefixKey}CountryId`,
                        dropdownList: beneficiaryCountriesOptions,
                        requesting: requestingBeneficiaryCountries,
                        onUpdate: onChangeBeneficiaryCountry,
                        isDisabled: entityIsInvalid,
                      })}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {makeSearchableDropDown({
                        inputId:
                          'sales-web-inspection-input-finalbeneficiarystate',
                        label: 'Provincia',
                        getInputProps,
                        inputName: `${prefixKey}StateId`,
                        dropdownList: beneficiaryStatesOptions,
                        requesting: requestingBeneficiaryStates,
                        isDisabled: isNotFromCostaRica || entityIsInvalid,
                        onUpdate: onChangeBeneficiaryState,
                      })}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {makeSearchableDropDown({
                        inputId:
                          'sales-web-inspection-input-finalbeneficiarycity',
                        label: 'Cantón',
                        getInputProps,
                        inputName: `${prefixKey}CityId`,
                        dropdownList: beneficiaryCityOptions,
                        requesting: false,
                        isDisabled: isNotFromCostaRica || entityIsInvalid,
                        onUpdate: onChangeBeneficiaryCity,
                      })}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {makeSearchableDropDown({
                        inputId:
                          'sales-web-inspection-input-finalbeneficiarytownship',
                        label: 'Distrito',
                        getInputProps,
                        inputName: `${prefixKey}TownshipId`,
                        dropdownList: beneficiaryTownshipOptions,
                        requesting: false,
                        isDisabled: isNotFromCostaRica || entityIsInvalid,
                      })}
                    </Grid>

                    <Grid item xs={12}>
                      {makeTextField({
                        inputId:
                          'sales-web-inspection-input-finalbeneficiaryaddressdetail',
                        label: 'Dirección exacta (otras señas)',
                        inputName: `${prefixKey}AddressDetail`,
                        getInputProps,
                        isDisabled: entityIsInvalid,
                        shouldUpdate: isAlphaNumericForAddress,
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}
            </>
          )}
        </Grid>
      </DefaultCard>
    </>
  );
};

export default FinalBeneficiaryCard;
